import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from './contexts/AppContext';
import { useAuth } from './contexts/AuthContext.jsx';
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import axiosInstance, { API_GET } from './config/axios';
import toast from "react-hot-toast";
import { getDefaultRouteForRole } from './utils/roleRedirect.js';
import QrLoginPanel from './components/auth/QrLoginPanel.jsx';
import { logoSecura } from './assets/assets';
import { isWebHost as isWebHostFn } from './utils/webHost';

// Utiliser l'instance axios centralisée (les intercepteurs sont déjà configurés dans config/axios.js)
const API_URL = API_GET;

const Spinner = () => (
  <svg
    className="animate-spin h-5 w-5 text-white inline-block"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    ></path>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { setIsLoggedIn, setIsRegistered, getUserData, userData } = useContext(AppContext);
  const { handleLogin } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isWebAuthnLoading, setIsWebAuthnLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  
  // États pour compatibilité avec l'ancien système
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // web.app-secura.com : Secura sur ordinateur, connexion par QR code d'abord (comme web.whatsapp.com)
  const isWebHost = isWebHostFn();
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const { t, i18n } = useTranslation(); // Use translation hook

  // Fonction pour changer de langue
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  // Détection taille écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Désactiver scroll sur mobile/tablette
  useEffect(() => {
    if (isMobileOrTablet) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isMobileOrTablet]);

  // Vérifier la validité de la session pour WebAuthn
  useEffect(() => {
    const checkSession = () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          setHasValidSession(payload.exp > now);
        } catch (error) {
          setHasValidSession(false);
        }
      } else {
        setHasValidSession(false);
      }
    };
    
    checkSession();
  }, []);

  // Désactiver la redirection automatique pour éviter les boucles infinies
  // avec les routes protégées. La redirection sera gérée uniquement après
  // une connexion réussie via handleSubmit ou LoginAutomatique


  /**
   * Suite commune à toutes les connexions (mot de passe, QR code depuis le téléphone) :
   * stockage de la session, contextes, puis redirection (ou `redirectOverride`, ex. un projet).
   */
  const finishLogin = async (data, redirectOverride = null) => {
    toast.success("Connexion réussie");
    
    // Vérifier que les données utilisateur sont présentes
    if (!data.user) {
      console.error('❌ Données utilisateur manquantes dans la réponse:', data);
      toast.error("Erreur: données utilisateur manquantes");
      setIsFormLoading(false);
      return;
    }
    
    // Stocker les données dans les deux systèmes pour compatibilité
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    // Mettre à jour AuthContext (utilisé par les routes protégées) EN PREMIER
    handleLogin(data.user, data.token);
    
    // Mettre à jour AppContext (pour la compatibilité)
    setIsLoggedIn(true);
    setIsRegistered(true);
    // Attendre les données employé (dont flags entreprise) avant navigation pour afficher l’icône pointage terrain tout de suite
    try {
      await getUserData(true);
    } catch (e) {
      console.warn('getUserData après login:', e);
    }
    setHasValidSession(true);
    setIsFormLoading(false);
    
    // Redirection basée sur le rôle après connexion en utilisant roleRedirect.js
    const userRole = data.user?.role?.nom || data.user?.role;
    console.log('🔄 Redirection en cours - Rôle détecté:', userRole);
    
    // Utiliser la fonction de redirection depuis roleRedirect.js
    const redirectPath = redirectOverride || getDefaultRouteForRole(userRole);
    console.log('🔄 Route de redirection calculée:', redirectPath, 'pour le rôle:', userRole);
    
    setTimeout(() => {
      try {
        console.log('🔄 Exécution de la redirection vers:', redirectPath);
        navigate(redirectPath, { replace: true });
      } catch (navError) {
        console.error('❌ Erreur lors de la redirection:', navError);
        toast.error("Erreur de redirection");
      }
    }, 100); // Réduire le délai pour une redirection plus rapide
  };

  /** Session reçue après scan du QR code sur le téléphone. */
  const handleQrApproved = (data) => {
    finishLogin(data, data.redirect || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Empêcher la propagation de l'événement
    
    // Empêcher toute soumission multiple
    if (isFormLoading) {
      console.warn('⚠️ Connexion déjà en cours, ignore la nouvelle tentative');
      return;
    }
    
    setIsFormLoading(true);

    // Vérifications avant envoi
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      setIsFormLoading(false);
      return;
    }

    console.log('🔍 Tentative de connexion avec:', { email, passwordLength: password?.length });

    try {
      // Vérifier que les champs ne sont pas vides après trim
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();
      
      if (!trimmedEmail || !trimmedPassword) {
        toast.error("Veuillez remplir tous les champs");
        setIsFormLoading(false);
        return;
      }

      const requestData = { 
        email: trimmedEmail, 
        password: trimmedPassword
      };
      
      console.log('🔍 Données envoyées au serveur:', { 
        email: requestData.email,
        passwordLength: requestData.password.length,
        hasEmail: !!requestData.email,
        hasPassword: !!requestData.password,
        emailExact: requestData.email,
        passwordPreview: requestData.password.substring(0, 3) + '...' // Afficher seulement les 3 premiers caractères pour sécurité
      });
      
      console.log('📤 Envoi de la requête POST /auth/login...');
      const response = await axiosInstance.post("/auth/login", requestData);
      console.log('✅ Réponse reçue du serveur:', {
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        success: response.data?.success,
        hasUser: !!response.data?.user,
        hasToken: !!response.data?.token
      });
      
      const { data } = response;

      if (data.success) {
        console.log('✅ Connexion réussie - Données utilisateur:', {
          userId: data.user?.id,
          email: data.user?.email,
          role: data.user?.role?.nom || data.user?.role,
          hasToken: !!data.token
        });
        await finishLogin(data);
      } else {
        console.warn('⚠️ Connexion échouée - data.success est false:', {
          success: data.success,
          message: data.message,
          data: data
        });
        toast.error(data.message || "Échec de connexion");
        setIsFormLoading(false);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la connexion:', error);
      console.error('❌ Type d\'erreur:', error.constructor.name);
      console.error('❌ Détails de l\'erreur:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        request: error.request ? 'Oui' : 'Non',
        response: error.response ? 'Oui' : 'Non',
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          data: error.config?.data,
          headers: error.config?.headers
        }
      });
      
      // Si on a une réponse du serveur, afficher le message d'erreur
      if (error.response?.data?.message) {
        console.error('❌ Message d\'erreur du serveur:', error.response.data.message);
      }
      
      // Gestion spécifique des erreurs 400 (Bad Request)
      if (error.response?.status === 400) {
        const serverMessage = error.response?.data?.message;
        const serverData = error.response?.data;
        
        console.error('❌ Réponse serveur 400 - Détails complets:', {
          status: error.response.status,
          statusText: error.response.statusText,
          message: serverMessage,
          data: serverData,
          success: serverData?.success,
          body: error.response?.data
        });
        
        // Afficher le message d'erreur du serveur si disponible
        if (serverMessage) {
          toast.error(serverMessage);
        } else {
          // Messages par défaut selon les raisons possibles
          const errorDetails = error.response?.data;
          if (errorDetails && typeof errorDetails === 'object') {
            // Si c'est un objet avec des propriétés spécifiques
            if (errorDetails.errors) {
              toast.error(`Erreur: ${JSON.stringify(errorDetails.errors)}`);
            } else {
              toast.error("Données de connexion invalides. Vérifiez votre email et mot de passe.");
            }
          } else {
            toast.error("Données de connexion invalides. Vérifiez votre email et mot de passe.");
          }
        }
      } else if (error.response?.status === 401) {
        toast.error("Email ou mot de passe incorrect");
      } else if (!error.response) {
        toast.error("Erreur de connexion au serveur. Vérifiez votre connexion internet.");
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Une erreur est survenue";
        toast.error(`Erreur de connexion: ${errorMessage}`);
      }
      setIsFormLoading(false);
    }
  };

  const LoginAutomatique = async () => {
    setIsWebAuthnLoading(true);

    try {
      // Appel direct à /auth/refresh-token comme dans Secure-Tech CORPORATE
      const { data } = await axiosInstance.post(
        "/auth/refresh-token",
        {},
        { withCredentials: true }
      );

      if (data.success) {
        toast.success("Poser votre pouce sur le capteur !");
        setIsLoggedIn(true);
        setIsRegistered(true);
        await getUserData();
        
        if (
          window.PublicKeyCredential &&
          await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        ) {
          try {
            const challenge = new Uint8Array(32);
            window.crypto.getRandomValues(challenge);

            const credential = await navigator.credentials.get({
              publicKey: {
                challenge,
                allowCredentials: [],
                userVerification: "required",
                transports: ["internal"]
              },
              mediation: "required"
            });

            if (credential) {
              toast.success("Déverrouillage biométrique réussi !");
            }

            // Redirection basée sur le rôle après connexion automatique
            setTimeout(() => {
              // Récupérer le rôle depuis userData (mis à jour par getUserData)
              const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
              const userRole = storedUser?.role?.nom || storedUser?.role;
              
              if (userRole === 'Employee' || userRole === 'Employé') {
                navigate('/employeeDashboard', { replace: true });
              } else if (userRole === 'AdminPanel') {
                navigate('/adminpanel', { replace: true });
              } else if (userRole === 'AdminRH' || userRole === 'AdminRH+1') {
                navigate('/dashboard', { replace: true });
              } else {
                // Fallback vers RoleBasedRedirect pour les autres cas
                navigate('/login', { replace: true });
              }
            }, 500);

          } catch (err) {
            console.warn("Échec biométrie :", err);
            toast.error("Échec biométrie !");
          }
          
          // Rediriger même si la biométrie échoue
          setTimeout(() => {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const userRole = storedUser?.role?.nom || storedUser?.role;
            
            if (userRole === 'Employee' || userRole === 'Employé') {
              navigate('/employeeDashboard', { replace: true });
            } else if (userRole === 'AdminPanel') {
              navigate('/adminpanel', { replace: true });
            } else if (userRole === 'AdminRH' || userRole === 'AdminRH+1') {
              navigate('/dashboard', { replace: true });
            } else {
              navigate('/login', { replace: true });
            }
          }, 500);
        } else {
          // Si la biométrie n'est pas disponible, rediriger quand même
          setTimeout(() => {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const userRole = storedUser?.role?.nom || storedUser?.role;
            
            if (userRole === 'Employee' || userRole === 'Employé') {
              navigate('/employeeDashboard', { replace: true });
            } else if (userRole === 'AdminPanel') {
              navigate('/adminpanel', { replace: true });
            } else if (userRole === 'AdminRH' || userRole === 'AdminRH+1') {
              navigate('/dashboard', { replace: true });
            } else {
              navigate('/login', { replace: true });
            }
          }, 500);
        }
      }
    } catch (err) {
      toast.error("Session expirée, veuillez vous reconnecter.");
      navigate("/password-fallback");
    } finally {
      setIsWebAuthnLoading(false);
    }
  };


  // web.app-secura.com sur ordinateur : QR code en plein écran, mot de passe en second choix
  if (!isMobileOrTablet && isWebHost && !usePasswordLogin) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
        style={{
          background: "linear-gradient(to bottom right, rgba(0, 102, 255, 0.2) 5%, rgba(0, 25, 255, 0.2) 100%)",
        }}
      >
        <img src={logoSecura} alt="Secura" className="mb-8 h-16 w-auto object-contain" />
        <QrLoginPanel onApproved={handleQrApproved} />
        <button
          type="button"
          onClick={() => setUsePasswordLogin(true)}
          className="mt-6 min-h-[44px] px-4 text-sm font-medium text-[#0026E2] hover:underline"
        >
          Se connecter avec email et mot de passe
        </button>
      </div>
    );
  }

  // Version mobile - utiliser le même style que login.jsx Employee
  if (isMobileOrTablet) {
    return (
      <div
        className="min-h-screen flex flex-col justify-end"
        style={{
          background: "linear-gradient(to bottom right, rgba(0, 102, 255, 0.2) 5%, rgba(0, 25, 255, 0.2) 100%)",
        }}
      >
        <div className="w-full rounded-t-[3rem] bg-white px-6 pt-8 pb-32 shadow-md">
          {/* Sélecteur de langue */}
          <div className="flex gap-8 mb-6 justify-center">
            <button 
              className={`text-lg font-medium pb-2 border-b-2 transition-colors duration-200 ${
                i18n.language === 'fr' 
                  ? 'text-gray-900 border-blue-500' 
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}
              onClick={() => changeLanguage('fr')}
            >
              FR
            </button>
            <button 
              className={`text-lg font-medium pb-2 border-b-2 transition-colors duration-200 ${
                i18n.language === 'en' 
                  ? 'text-gray-900 border-blue-500' 
                  : 'text-gray-600 border-transparent hover:text-gray-900'
              }`}
              onClick={() => changeLanguage('en')}
            >
              EN
            </button>
          </div>

          <h2 className="text-center text-xl font-black text-gray-900 mb-1">Se connecter</h2>
          <p className="text-center text-sm text-gray-500 mb-6">Se connecter à mon compte</p>

          {/* Messages d'erreur/succès */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1">Email</label>
              <input
                type="email"
                placeholder="simbokoly@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isFormLoading || isWebAuthnLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {emailError && <span className="text-xs text-red-600 mt-1">{emailError}</span>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold text-gray-900">Mot de passe</label>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      navigate("/reset-password", { replace: true });
                    } catch (error) {
                      console.error('Erreur navigation reset-password:', error);
                    }
                  }}
                  className="text-xs text-[#0026E2] hover:underline font-medium"
                  disabled={isFormLoading || isWebAuthnLoading}
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isFormLoading || isWebAuthnLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              />
                <span
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-500 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                </span>
              </div>
              {passwordError && <span className="text-xs text-red-600 mt-1">{passwordError}</span>}
            </div>

            {/* Options */}
            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t('login.rememberMe')}</span>
            </div>

            <button
              type="submit"
              disabled={isFormLoading}
              className={`w-full bg-[#0026E2] text-white font-semibold py-3.5 rounded-xl flex justify-center items-center gap-2 ${isFormLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isFormLoading ? (
                <>
                  <Spinner /> Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>

          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="mx-4 text-sm text-gray-500">OU</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <button
            onClick={LoginAutomatique}
            disabled={isWebAuthnLoading}
            className={`w-full bg-blue-500 text-white font-semibold py-3.5 rounded-xl flex justify-center items-center gap-2 ${isWebAuthnLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isWebAuthnLoading ? (
              <>
                <Spinner /> Déverrouillage...
              </>
            ) : (
              "Déverrouiller avec phone"
            )}
          </button>

          <div className="mt-6 text-center text-sm text-gray-600">
            Pas de compte ?{" "}
            <button
              type="button"
              onClick={() => {
                try {
                  navigate("/register", { replace: true });
                } catch (error) {
                  console.error('Erreur navigation register:', error);
                }
              }}
              className="text-[#0026E2] hover:underline font-medium"
              disabled={isFormLoading || isWebAuthnLoading}
            >
              Créer un compte
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Version desktop - design split avec formulaire à gauche
  return (
    <div className="min-h-screen flex overflow-hidden font-sans">
      {/* Partie gauche - Formulaire (environ 60%) */}
      <div className="flex-1 flex flex-col min-h-screen bg-white md:flex-1" style={{flex: '0 0 50%'}}>
        
        {/* Sélecteur de langue */}
        <div className="flex gap-8 mb-6 mt-6 justify-start px-32">
          <button 
            className={`text-lg font-medium pb-2 border-b-2 transition-colors duration-200 ${
              i18n.language === 'fr' 
                ? 'text-gray-900 border-blue-500' 
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
            onClick={() => changeLanguage('fr')}
          >
            FR
          </button>
          <button 
            className={`text-lg font-medium pb-2 border-b-2 transition-colors duration-200 ${
              i18n.language === 'en' 
                ? 'text-gray-900 border-blue-500' 
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
            onClick={() => changeLanguage('en')}
          >
            EN
          </button>
        </div>

        {/* Contenu du formulaire centré verticalement */}
        <div className="flex-1 flex flex-col justify-center items-start">
          <div className="w-full max-w-2xl px-32">
          
          {/* Titre */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-left">Se connecter</h1>
          <p className="text-sm text-gray-500 mb-8 text-left">Se connecter à mon compte</p>

          {/* Messages d'erreur/succès */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 text-sm">
              {success}
            </div>
          )}
          
          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Champ Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                {t('login.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="simbokoly@gmail.com"
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-gray-400"
                disabled={isFormLoading || isWebAuthnLoading}
                required
              />
              {emailError && <span className="text-xs text-red-600 mt-1">{emailError}</span>}
            </div>
            
            {/* Champ Mot de passe */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Créer un mot de passe"
                  className="w-full px-5 py-4 pr-12 border border-gray-300 rounded-xl text-base transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 hover:border-gray-400"
                  disabled={isFormLoading || isWebAuthnLoading}
                  required
                />
                <span
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-500 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                </span>
              </div>
              {passwordError && <span className="text-xs text-red-600 mt-1">{passwordError}</span>}
            </div>
            
            {/* Options */}
            <div className="flex justify-between items-center pt-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{t('login.rememberMe')}</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigate("/reset-password", { replace: true });
                  } catch (error) {
                    console.error('Erreur navigation reset-password desktop:', error);
                  }
                }}
                className="text-xs text-[#0026E2] hover:underline font-medium"
                disabled={isFormLoading || isWebAuthnLoading}
              >
                Mot de passe oublié ?
              </button>
            </div>
            
            {/* Bouton de connexion */}
            <button 
              type="submit" 
              disabled={isFormLoading || isWebAuthnLoading}
              className={`w-full bg-[#0026E2] text-white font-semibold py-3.5 rounded-xl flex justify-center items-center gap-2 ${
                isFormLoading || isWebAuthnLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              {isFormLoading ? (
                <>
                  <Spinner />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>
          {!isWebHost && (
            <p className="mt-8 text-sm text-gray-600">
              Pas encore client ?{' '}
              <Link to="/creer-entreprise" className="text-[#0026E2] hover:underline font-medium">Créer mon entreprise</Link>
              {' · '}
              <Link to="/" className="text-gray-500 hover:underline">Retour au site</Link>
            </p>
          )}
          </div>
        </div>
      </div>

      {/* Partie droite - Grand rectangle bleu aux bords arrondis (environ 50%) */}
      <div 
        className="hidden md:flex flex-1 relative min-h-screen overflow-hidden rounded-l-[6rem]" 
        style={{
          flex: '0 0 50%',
          background: "linear-gradient(to bottom right, rgba(0, 102, 255, 0.2) 5%, rgba(0, 25, 255, 0.2) 100%)"
        }}
      >
        {/* Connexion par QR code : réservée à web.app-secura.com */}
        {isWebHost ? (
          <div className="w-full flex items-center justify-center px-12">
            <QrLoginPanel onApproved={handleQrApproved} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Login;
